import cn from "clsx";
import { KeyTextField, RichTextField } from "@prismicio/types";
import React from "react";
import Field from "@components/ui/Forms/Field/Field";
import { useTranslation } from "next-i18next";
import {
  useGlobalMessaging,
  useSchemaMessages,
  useUI,
} from "@middleware/hooks";
import { FieldValues, SubmitHandler, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import {
  FieldPropsType,
  IEvaluation,
  IObject,
  TFunctionFieldProps,
} from "@middleware/types";
import Api from "@middleware/api";
import { HttpStatusCode } from "axios";
import { getUtmSessions } from "@middleware/helpers/global/sessions";
import { Button, Text } from "@components/ui";
import { PrismicRichText } from "@prismicio/react";
import { TitlePage } from "@components/commun/TitlePage/TitlePage";
import style from "./RegisterByMailForm.module.scss";
import { defaultValues, fields, getSchema } from "./fields";

interface Props {
  title?: RichTextField;
  text?: RichTextField;
  submitText?: KeyTextField;
  className?: string;
  afterSubmit?: () => void;
  utms?: IObject;
}
export default function RegisterByMailForm({
  title,
  text,
  submitText,
  afterSubmit,
  className,
  utms,
}: Props) {
  const { t } = useTranslation();
  const { setMessage } = useGlobalMessaging();
  const { showLoader, hideLoader } = useUI();
  const customSchema = getSchema(useSchemaMessages());

  const {
    register,
    handleSubmit,
    formState: { isSubmitting, errors },
    control,
  } = useForm({
    mode: "onBlur",
    defaultValues: defaultValues as FieldValues,
    resolver: yupResolver<FieldValues>(customSchema),
  });

  const getFieldProps = (
    field: FieldPropsType<FieldValues, TFunctionFieldProps>,
  ) => {
    const fieldProps = {
      name: field.name,
      label: t(typeof field.label === "string" ? field.label : ""),
      className: field.class,
      placeholder: t(field.placeholder ?? "").toString(),
      register: register,
      control: control,
      type: field.type,
      error: errors[field.name]?.message as string,
    };

    return fieldProps;
  };

  const onSubmit: SubmitHandler<FieldValues> = async (datas) => {
    showLoader();

    const utmParams = getUtmSessions();
    const response = await Api.authentication.registerByEvaluation(
      datas as IEvaluation,
      utms ? utms : utmParams,
    );
    if (response.status === HttpStatusCode.NoContent) {
      setMessage({ type: "SUCCESS", text: t("success.congratulation") });
    } else {
      const errorMessage: string = (response.data as { title: string }).title;
      setMessage({ type: "ERROR", text: errorMessage });
    }
    if (afterSubmit) afterSubmit();
    hideLoader();
  };

  return (
    <div className={cn(style.blocForm, className)}>
      {title && (
        <TitlePage className={style.title}>
          <PrismicRichText field={title} />
        </TitlePage>
      )}
      <form onSubmit={handleSubmit(onSubmit)} noValidate>
        <div className="bilan-form">
          {fields.map((field, stepIndex) => {
            const fieldProps = getFieldProps(field);

            return (
              <div
                className={cn(style.filedRow, style[field.name])}
                key={stepIndex}
              >
                <Field variant={field.type} props={fieldProps} />
              </div>
            );
          })}
          {text && (
            <div className={style.text}>
              <Text>
                <PrismicRichText field={text} />
              </Text>
            </div>
          )}
          <div className={style.action}>
            <Button
              type="submit"
              variant="default"
              className={cn(style.submit, "rounded")}
              puce={false}
              disabled={isSubmitting}
            >
              {submitText}
            </Button>
          </div>
        </div>
      </form>
    </div>
  );
}
